import React, { useState, useEffect } from 'react';
import CustomAlert from '../components/CustomAlert';
import useCustomAlert from '../hooks/useCustomAlert';
import { getAllTutors, subscribeToSchedules, updateScheduleStatus, deleteSchedule, getScheduleMemos, addSchedule, expireTrialLessons, deleteScheduleMemos, subscribeToMemos } from '../services/firestoreService';
import '../styles/ScheduleManagement.css';

const ScheduleManagement = ({ user }) => {
  const { alertState, showSuccess, showError, showWarning, showConfirm, showInfo } = useCustomAlert();
  const [scheduleData, setScheduleData] = useState(null); // null로 초기화
  const [memos, setMemos] = useState([]); // 메모 상태 추가
  const [pendingMemos, setPendingMemos] = useState([]); // 매칭 대기 메모 상태 추가
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [tutors, setTutors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [lessonType, setLessonType] = useState(''); // 수업 종류: 'regular' (정규수업), 'trial' (체험수업)
  const [lessonDate, setLessonDate] = useState(''); // 체험수업 날짜
  const [isEditingStudent, setIsEditingStudent] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    tutorName: '',
    dayId: '',
    timeSlot: ''
  });
  const [showTutorSuggestions, setShowTutorSuggestions] = useState(false);
  const [filteredTutors, setFilteredTutors] = useState([]);

  // 사유별 텍스트 매핑
  const getReasonText = (reason) => {
    const reasonMap = {
      'schedule_conflict': '스케줄 충돌',
      'student_request': '학생 요청',
      'personal_matter': '개인 사정',
      'emergency': '긴급 상황',
      'system_error': '시스템 오류',
      'other': '기타'
    };
    return reasonMap[reason] || '기타';
  };

  // 공통 날짜 계산 함수 (관리자 기준 - 월요일 시작)
  const getWeekCalculation = (baseDate = new Date()) => {
    const today = new Date(baseDate);
    const currentDay = today.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일
    
    // 이번 주 월요일 계산 (월요일을 주의 시작으로 함)
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay; // 일요일인 경우 -6, 나머지는 1-currentDay
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    
    return {
      monday,
      getDateForDayOfWeek: (dayOfWeek) => {
        const dayOfWeekMap = {
          'monday': 0, 'tuesday': 1, 'wednesday': 2, 'thursday': 3,
          'friday': 4, 'saturday': 5, 'sunday': 6
        };
        const targetDayIndex = dayOfWeekMap[dayOfWeek];
        const targetDate = new Date(monday);
        targetDate.setDate(monday.getDate() + targetDayIndex);
        return targetDate;
      }
    };
  };

  // 매칭 대기 메모의 불가능한 날짜 체크
  const checkUnavailableDate = (tutorId, dayOfWeek, timeSlot, lessonDate) => {
    if (!lessonDate) return false; // 체험수업이 아니면 체크하지 않음
    
    // 해당 튜터의 매칭대기 메모 찾기
    const tutorPendingMemo = pendingMemos.find(memo => 
      memo && 
      memo.tutorId === tutorId && 
      memo.dayOfWeek === dayOfWeek && 
      (memo.timeSlot === timeSlot || memo.time === timeSlot)
    );

    if (!tutorPendingMemo || !tutorPendingMemo.unavailableDates) {
      return false;
    }

    // 선택된 수업 날짜가 불가능한 날짜에 포함되는지 확인
    const selectedDate = new Date(lessonDate);
    selectedDate.setHours(0, 0, 0, 0);
    
    const isUnavailable = tutorPendingMemo.unavailableDates.some(dateStr => {
      const unavailableDate = new Date(dateStr);
      unavailableDate.setHours(0, 0, 0, 0);
      return unavailableDate.getTime() === selectedDate.getTime();
    });
    
    return isUnavailable;
  };

  // 요일 리스트
  const weekDays = [
    { id: 'monday', name: '월요일', short: '월' },
    { id: 'tuesday', name: '화요일', short: '화' },
    { id: 'wednesday', name: '수요일', short: '수' },
    { id: 'thursday', name: '목요일', short: '목' },
    { id: 'friday', name: '금요일', short: '금' },
    { id: 'saturday', name: '토요일', short: '토' },
    { id: 'sunday', name: '일요일', short: '일' }
  ];
  
  // 시간대 리스트
  const timeSlots = [
    '08:00-09:00', '09:00-10:00', '10:00-11:00', '11:00-12:00',
    '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00',
    '16:00-17:00', '17:00-18:00', '18:00-19:00', '19:00-20:00',
    '20:00-21:00', '21:00-22:00'
  ];

  // 튜터 정보 로드
  useEffect(() => {
    const loadTutors = async () => {
      try {
        const tutorsData = await getAllTutors();
        setTutors(tutorsData);
        console.log('👨‍🏫 튜터 정보 로드:', tutorsData);
      } catch (error) {
        console.error('튜터 정보 로드 오류:', error);
      }
    };
    
    loadTutors();
  }, []);

  // 실시간 스케줄 구독
  useEffect(() => {
    setLoading(true);
    
    const unsubscribe = subscribeToSchedules((allSchedules) => {
      // allSchedules가 undefined이거나 배열이 아닌 경우 처리
      if (!allSchedules || !Array.isArray(allSchedules)) {
        console.log('📊 스케줄 데이터가 유효하지 않습니다:', allSchedules);
        setScheduleData({});
        setLoading(false);
        return;
      }
      
      console.log('📊 실시간 스케줄 데이터 업데이트:', allSchedules);
      console.log('📊 스케줄 개수:', allSchedules.length);
      console.log('📊 스케줄 상태별 개수:', {
        pending: allSchedules.filter(s => s && s.status === 'pending').length,
        confirmed: allSchedules.filter(s => s && s.status === 'confirmed').length,
        registered: allSchedules.filter(s => s && s.status === 'registered').length
      });
      
      // 메모 등록된 스케줄들의 메모 정보 가져오기
      const registeredSchedules = allSchedules.filter(s => s && s.status === 'registered');
      const memoPromises = registeredSchedules.map(async (schedule) => {
        try {
          const memos = await getScheduleMemos(schedule.id);
          return { scheduleId: schedule.id, memoInfo: memos.length > 0 ? memos[0] : null };
        } catch (error) {
          console.error(`메모 정보 로드 오류 (스케줄 ${schedule.id}):`, error);
          return { scheduleId: schedule.id, memoInfo: null };
        }
      });
      
      Promise.all(memoPromises).then(memoResults => {
        const memoMap = new Map();
        memoResults.forEach(({ scheduleId, memoInfo }) => {
          if (memoInfo) {
            memoMap.set(scheduleId, memoInfo);
          }
        });
        
        // 스케줄 데이터를 요일별, 시간대별로 그룹화
        const groupedSchedules = {};
        
        allSchedules.forEach(schedule => {
          if (!schedule || !schedule.dayOfWeek || !schedule.timeSlot) {
            console.warn('유효하지 않은 스케줄 데이터:', schedule);
            return;
          }
          
          const { dayOfWeek, timeSlot } = schedule;
          
          // timestamp 필드들이 Firestore Timestamp 객체인지 확인하고 변환
          const processedSchedule = {
            ...schedule,
            requestedAt: schedule.requestedAt || schedule.createdAt,
            confirmedAt: schedule.confirmedAt,
            updatedAt: schedule.updatedAt,
            memoInfo: memoMap.get(schedule.id) || null
          };
          
          if (!groupedSchedules[dayOfWeek]) {
            groupedSchedules[dayOfWeek] = {};
          }
          
          if (!groupedSchedules[dayOfWeek][timeSlot]) {
            groupedSchedules[dayOfWeek][timeSlot] = [];
          }
          
          groupedSchedules[dayOfWeek][timeSlot].push(processedSchedule);
        });
        
        console.log('📊 그룹화된 스케줄 데이터:', groupedSchedules);
        console.log('📊 요일별 스케줄 개수:', Object.keys(groupedSchedules).map(day => ({
          day,
          count: Object.values(groupedSchedules[day]).reduce((total, timeSlots) => total + timeSlots.length, 0)
        })));
        setScheduleData(groupedSchedules);
        setLoading(false);
      });
    }, (error) => {
      console.error('스케줄 실시간 구독 오류:', error);
      showError('스케줄 데이터를 불러오는데 실패했습니다.');
      setScheduleData({});
      setLoading(false);
    });

    // 컴포넌트 언마운트 시 구독 해제
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

// 실시간 메모 구독
useEffect(() => {
  const unsubscribeMemos = subscribeToMemos((memosData) => {
    console.log('📝 실시간 메모 데이터 업데이트:', memosData);
    setMemos(memosData);
    
    // 매칭 대기 메모만 별도로 필터링
    const pendingMemosData = memosData.filter(memo => 
      memo && memo.memoType === 'pending_memo'
    );
    console.log('⏳ 매칭 대기 메모 업데이트:', pendingMemosData);
    console.log('🔍 강경효 튜터 매칭 대기 메모:', pendingMemosData.filter(m => m.tutorName === '강경효'));
    setPendingMemos(pendingMemosData);
  }, (error) => {
    console.error('메모 실시간 구독 오류:', error);
  });

  // 컴포넌트 언마운트 시 구독 해제
  return () => {
    if (unsubscribeMemos) unsubscribeMemos();
  };
}, []);


// 매칭대기 스케줄의 메모 정보 확인
const getPendingMemoInfo = (schedule) => {
  // 실제 상태 계산
  const actualStatus = getActualScheduleStatus(schedule);
  
  // 강경효 튜터 디버깅용 전역 로그
  if (schedule?.tutorName === '강경효' && schedule?.dayOfWeek === 'tuesday' && schedule?.timeSlot === '11:00-12:00') {
    console.log('🚨 getPendingMemoInfo 호출 - 강경효 튜터:', {
      actualStatus,
      memosLength: memos.length,
      pendingMemosLength: pendingMemos.length,
      willReturn: (actualStatus !== 'pending' && actualStatus !== 'pending_with_memo') ? 'null (상태 불일치)' : '계속 진행'
    });
  }
  
  if (!schedule || (actualStatus !== 'pending' && actualStatus !== 'pending_with_memo')) {
    return null;
  }

  // 해당 요일과 시간대의 매칭대기 메모 찾기
  const pendingMemo = memos.find(memo => 
    memo && 
    memo.tutorId === schedule.tutorId &&
    memo.dayOfWeek === schedule.dayOfWeek && 
    (memo.timeSlot === schedule.timeSlot || memo.time === schedule.timeSlot) &&
    memo.memoType === 'pending_memo'
  );

  // 강경효 튜터의 경우 상세 디버깅
  if (schedule.tutorName === '강경효' && schedule.dayOfWeek === 'tuesday' && schedule.timeSlot === '11:00-12:00') {
    console.log('🔍 강경효 메모 검색 디버깅:', {
      scheduleData: {
        tutorId: schedule.tutorId,
        tutorName: schedule.tutorName,
        dayOfWeek: schedule.dayOfWeek,
        timeSlot: schedule.timeSlot
      },
      pendingMemo: pendingMemo,
      allMemos: memos.filter(m => m.tutorName === '강경효'),
      memosCount: memos.length
    });
  }

  if (!pendingMemo) {
    return null;
  }

  // 불가능한 날짜들 가져오기
  const unavailableDates = pendingMemo.unavailableDates || (pendingMemo.unavailableDate ? [pendingMemo.unavailableDate] : []);
  
  if (unavailableDates.length === 0) {
    return {
      memo: pendingMemo,
      isAnyWeekUnavailable: false,
      unavailableWeeks: [],
      unavailableDates,
      reason: pendingMemo.memo || pendingMemo.reason
    };
  }

  // 공통 날짜 계산 사용
  const { nextMonday, getDateForDayOfWeek } = getWeekCalculation();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // 다음 주 해당 요일 날짜
  const nextWeekDate = getDateForDayOfWeek(schedule.dayOfWeek);
  nextWeekDate.setDate(nextWeekDate.getDate() + 7); // 다음 주로

  // 불가능한 날짜들 중에서 다음 주 이후에 오는 날짜가 있는지 확인
  let isAnyWeekUnavailable = false;
  let unavailableWeeks = [];

  unavailableDates.forEach(dateStr => {
    const unavailableDate = new Date(dateStr);
    unavailableDate.setHours(0, 0, 0, 0);
    
    // 며칠 후인지 계산
    const diffDays = Math.ceil((unavailableDate - today) / (1000 * 60 * 60 * 24));
    
    // 7일 이내에 불가능한 날짜가 있으면 매칭 제한 (1주일 전부터 제한)
    if (diffDays <= 7 && diffDays > 0) {
      isAnyWeekUnavailable = true;
      unavailableWeeks.push(`${diffDays}일 후`);
    }
  });

  return {
    memo: pendingMemo,
    isAnyWeekUnavailable,
    unavailableWeeks,
    unavailableDates,
    reason: pendingMemo.memo || pendingMemo.reason
  };
};

// 불가능한 날짜들을 간단하게 포맷팅하는 함수
const formatUnavailableDates = (unavailableDates) => {
  if (!unavailableDates || unavailableDates.length === 0) return '';
  
  const formattedDates = unavailableDates
    .slice() // 원본 배열을 변경하지 않기 위해 복사
    .sort((a, b) => new Date(a) - new Date(b)) // 오름차순 정렬
    .map(dateStr => {
      const date = new Date(dateStr);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }).join(', ');
  
  return formattedDates;
};

// 스케줄의 실제 상태를 동적으로 계산하는 함수
const getActualScheduleStatus = (schedule) => {
  // pending_with_memo가 아니면 원래 상태 그대로 반환
  if (schedule.status !== 'pending_with_memo') {
    return schedule.status;
  }

  // 해당 스케줄의 매칭대기 메모 찾기
  const pendingMemo = memos.find(memo => 
    memo && 
    memo.tutorId === schedule.tutorId &&
    memo.dayOfWeek === schedule.dayOfWeek && 
    memo.timeSlot === schedule.timeSlot &&
    memo.memoType === 'pending_memo'
  );

  if (!pendingMemo || !pendingMemo.unavailableDates) {
    return 'pending'; // 메모가 없으면 일반 pending으로
  }

  // 공통 날짜 계산 사용
  const { nextMonday, getDateForDayOfWeek } = getWeekCalculation();
  
  // 다음 주 해당 요일 날짜
  const nextWeekDate = getDateForDayOfWeek(schedule.dayOfWeek);
  nextWeekDate.setDate(nextWeekDate.getDate() + 7); // 다음 주로

  // 불가능한 날짜들 중에서 다음 주 이후에 오는 날짜가 있는지 확인
  const hasUpcomingUnavailableDate = pendingMemo.unavailableDates.some(dateStr => {
    const unavailableDate = new Date(dateStr);
    unavailableDate.setHours(0, 0, 0, 0);
    return unavailableDate >= nextWeekDate;
  });

  // 다음 주 이후에 불가능한 날짜가 있으면 pending_with_memo, 없으면 pending
  return hasUpcomingUnavailableDate ? 'pending_with_memo' : 'pending';
};

// 스케줄 클릭 처리 (관리자는 모든 스케줄 클릭 가능)
const handleScheduleClick = async (dayId, timeSlot, schedule) => {
  try {
    // 매칭대기 메모가 있는 경우 알림만 표시(매칭 제한 없음)
    const pendingMemoInfo = getPendingMemoInfo(schedule);
    if (pendingMemoInfo) {
      const formattedDates = formatUnavailableDates(pendingMemoInfo.unavailableDates);
      showInfo(`🚫 수업 불가능 안내

📅 수업 불가능 날짜: ${formattedDates || '날짜 정보 없음'}
📋 사유: ${pendingMemoInfo.reason || '사유 없음'}

확인을 누르면 계속 진행할 수 있습니다.`);
      // 매칭/삭제 제한 없이 그대로 진행
    }
    
    // 메모가 있는 스케줄인 경우 메모 정보 가져오기
    let memoInfo = null;
    if (schedule.status === 'registered') {
      const memos = await getScheduleMemos(schedule.id);
      if (memos.length > 0) {
        memoInfo = memos[0]; // 가장 최근 메모
      }
    } else if (pendingMemoInfo) {
      // 매칭대기 메모 정보를 모달에서도 볼 수 있도록 주입
      memoInfo = {
        ...pendingMemoInfo.memo,
        reason: pendingMemoInfo.reason,
        unavailableDates: pendingMemoInfo.unavailableDates,
        memoType: 'pending_memo'
      };
    }
    
    const actualStatus = schedule.status; // 전달된 schedule은 이미 실제 상태로 전달됨

    setSelectedSchedule({ 
      dayId, 
      timeSlot, 
      ...schedule,
      status: actualStatus,
      memoInfo 
    });
    // 매칭 대기 상태는 모든 입력값 초기화
    if (actualStatus === 'pending') {
      setStudentName('');
      setLessonType('');
      setLessonDate('');
    } else {
      // 확정/등록 상태는 기존 값 유지
      setStudentName(schedule.studentName || '');
      setLessonType(schedule.lessonType || 'regular');
      setLessonDate(schedule.lessonDate || '');
    }
    setIsEditingStudent(false);
    setShowModal(true);
  } catch (error) {
    console.error('스케줄 정보 로드 오류:', error);
    showError('스케줄 정보를 불러오는데 실패했습니다.');
  }
};

  // 스케줄 삭제 (취소) 처리
  const handleDeleteSchedule = async (dayId, timeSlot, scheduleId) => {
    try {
      // 스케줄 관련 메모 먼저 삭제
      const deletedMemosCount = await deleteScheduleMemos(scheduleId);
      
      // Firestore에서 실제 스케줄 삭제
      await deleteSchedule(scheduleId);
      
      // 로컬 상태 업데이트
      setScheduleData(prev => {
        const dayData = prev[dayId];
        if (!dayData) return prev;
        
        const timeData = dayData[timeSlot];
        if (!timeData || !Array.isArray(timeData)) return prev;
        
        // 필터링된 스케줄 목록
        const filteredSchedules = timeData.filter(schedule => schedule && schedule.id !== scheduleId);
        
        // 필터링된 결과가 빈 배열이면 해당 시간대를 완전히 제거
        if (filteredSchedules.length === 0) {
          const newDayData = { ...dayData };
          delete newDayData[timeSlot];
          
          // 해당 요일의 모든 시간대가 비어있으면 요일도 제거
          if (Object.keys(newDayData).length === 0) {
            const newScheduleData = { ...prev };
            delete newScheduleData[dayId];
            return newScheduleData;
          }
          
          return {
            ...prev,
            [dayId]: newDayData
          };
        }
        
        // 스케줄이 남아있으면 업데이트
        return {
          ...prev,
          [dayId]: {
            ...dayData,
            [timeSlot]: filteredSchedules
          }
        };
      });
      
      // 삭제 결과 메시지
      if (deletedMemosCount > 0) {
        await showSuccess(`스케줄이 삭제되었습니다. 관련된 메모도 삭제됩니다.`);
      } else {
        await showSuccess('스케줄이 삭제되었습니다.');
      }
    } catch (error) {
      console.error('스케줄 삭제 오류:', error);
      await showError('스케줄 삭제에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 실제 Firebase에서 튜터 목록 가져오기
  const [tutorList, setTutorList] = useState([]);

  // 컴포넌트 마운트 시 튜터 목록 로드
  useEffect(() => {
    const loadTutors = async () => {
      try {
        const tutors = await getAllTutors();
        setTutorList(tutors);
      } catch (error) {
        console.error('튜터 목록 로드 오류:', error);
        await showError('튜터 목록을 불러오는데 실패했습니다.');
      }
    };
    loadTutors();
  }, []);

  // 튜터 검증 함수
  const validateTutor = (tutorName) => {
    const trimmedName = tutorName.trim();
    if (!trimmedName) {
      return { isValid: false, error: '튜터명을 입력해주세요.' };
    }

    const foundTutor = tutorList.find(tutor => 
      tutor.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (!foundTutor) {
      return { 
        isValid: false, 
        error: `"${trimmedName}" 튜터를 찾을 수 없습니다.\n정확한 튜터명을 입력하거나 자동완성을 이용해주세요.` 
      };
    }

    return { isValid: true, tutor: foundTutor };
  };

  // 관리자 스케줄 등록
  const handleAddSchedule = async () => {
    if (!newSchedule.tutorName.trim() || !newSchedule.dayId || !newSchedule.timeSlot) {
      await showError('모든 항목을 입력/선택해주세요.', '입력 오류');
      return;
    }

    // 튜터 검증
    const validation = validateTutor(newSchedule.tutorName);
    if (!validation.isValid) {
      await showError(validation.error, '튜터 확인 오류');
      return;
    }

    const { tutor } = validation;
    
    try {
      // Firestore에 실제 저장
      const scheduleData = {
        tutorId: (tutor.email || '').toLowerCase(),
        tutorName: tutor.name,
        tutorEmail: tutor.email,
        dayOfWeek: newSchedule.dayId,
        timeSlot: newSchedule.timeSlot,
        status: 'pending', // 매칭 대기 상태로 등록
        requestedBy: (user?.email || 'admin'), // 관리자가 등록
        requestedAt: new Date().toISOString() // 신청 시간 추가
      };

      const scheduleId = await addSchedule(scheduleData);
      
      // 로컬 상태 업데이트 (ID 포함)
      const scheduleItem = {
        id: scheduleId,
        ...scheduleData
      };

      setScheduleData(prev => ({
        ...prev,
        [newSchedule.dayId]: {
          ...prev[newSchedule.dayId],
          [newSchedule.timeSlot]: [
            ...(prev[newSchedule.dayId]?.[newSchedule.timeSlot] || []),
            scheduleItem
          ]
        }
      }));

      setShowAddModal(false);
      setNewSchedule({ tutorName: '', dayId: '', timeSlot: '' });
      setShowTutorSuggestions(false);
      setFilteredTutors([]);
      await showSuccess(`${tutor.name} 튜터의 스케줄이 매칭 대기 상태로 등록되었습니다!\n\n📝 등록 정보:\n- 튜터: ${tutor.name} (${tutor.email})\n- 요일: ${weekDays.find(d => d.id === newSchedule.dayId)?.name}\n- 시간: ${newSchedule.timeSlot}\n\n💡 등록된 스케줄을 클릭하여 학생을 배정할 수 있습니다.`, '스케줄 등록 완료');
    } catch (error) {
      console.error('스케줄 등록 오류:', error);
      await showError('스케줄 등록에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 튜터명 입력 시 자동완성 필터링
  const handleTutorNameChange = (value) => {
    setNewSchedule(prev => ({ ...prev, tutorName: value }));
    
    if (value.trim()) {
      if (tutorList && Array.isArray(tutorList)) {
        const filtered = tutorList.filter(tutor => 
          tutor.name && tutor.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredTutors(filtered);
        setShowTutorSuggestions(filtered.length > 0);
      } else {
        setFilteredTutors([]);
        setShowTutorSuggestions(false);
      }
    } else {
      setFilteredTutors([]);
      setShowTutorSuggestions(false);
    }
  };

  // 튜터 선택
  const handleTutorSelect = (tutor) => {
    setNewSchedule(prev => ({ ...prev, tutorName: tutor.name }));
    setShowTutorSuggestions(false);
    setFilteredTutors([]);
  };

  // 스케줄 확정
  const handleConfirmSchedule = async () => {
    console.log('🎯 학생 배정 시작:', {
      studentName: studentName,
      lessonType: lessonType,
      lessonDate: lessonDate,
      selectedSchedule: selectedSchedule
    });

    if (!studentName.trim()) {
      await showError('학생명을 입력해주세요.', '입력 오류');
      return;
    }

    // 체험수업인 경우 날짜 필수
    if (lessonType === 'trial' && !lessonDate) {
      await showError('체험수업의 경우 수업 날짜를 선택해주세요.', '입력 오류');
      return;
    }

    // 매칭 대기 메모의 불가능한 날짜 체크
    if (lessonType === 'trial' && lessonDate) {
      const { dayId, timeSlot } = selectedSchedule;
      const tutorId = selectedSchedule.tutorId;
      
      if (checkUnavailableDate(tutorId, dayId, timeSlot, lessonDate)) {
        await showError(
          `선택하신 날짜(${lessonDate})는 해당 튜터가 매칭 대기 메모에서 불가능하다고 표시한 날짜입니다.\n다른 날짜를 선택해주세요.`, 
          '매칭 불가능한 날짜'
        );
        return;
      }
    }

    try {
      console.log('🔄 스케줄 업데이트 시작...');
      const { dayId, timeSlot, id } = selectedSchedule;
      
      // Firestore에 실제 업데이트 (관리자 이름 포함)
      await updateScheduleStatus(id, 'confirmed', studentName.trim(), `${user.name} 관리자`, lessonType, lessonDate);
      
      // 로컬 상태 업데이트
      setScheduleData(prev => ({
        ...prev,
        [dayId]: {
          ...prev[dayId],
          [timeSlot]: prev[dayId][timeSlot].map(schedule => 
            schedule.id === id 
              ? {
                  ...schedule,
                  status: 'confirmed',
                  studentName: studentName.trim(),
                  lessonType: lessonType, // 수업 종류 추가
                  lessonDate: lessonDate, // 체험수업 날짜 추가
                  confirmedAt: new Date().toISOString(),
                  confirmedBy: 'admin'
                }
              : schedule
          )
        }
      }));

      setShowModal(false);
      setSelectedSchedule(null);
      setStudentName('');
      await showSuccess('스케줄이 확정되었습니다!');
    } catch (error) {
      console.error('스케줄 확정 오류:', error);
      await showError('스케줄 확정에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 학생 정보 수정
  const handleUpdateStudent = async () => {
    if (!studentName.trim()) {
      await showError('학생명을 입력해주세요.', '입력 오류');
      return;
    }

    // 체험수업인 경우 날짜 필수
    if (lessonType === 'trial' && !lessonDate) {
      await showError('체험수업의 경우 수업 날짜를 선택해주세요.', '입력 오류');
      return;
    }

    // 매칭 대기 메모의 불가능한 날짜 체크
    if (lessonType === 'trial' && lessonDate) {
      const { dayId, timeSlot } = selectedSchedule;
      const tutorId = selectedSchedule.tutorId;
      
      if (checkUnavailableDate(tutorId, dayId, timeSlot, lessonDate)) {
        await showError(
          `선택하신 날짜(${lessonDate})는 해당 튜터가 매칭 대기 메모에서 불가능하다고 표시한 날짜입니다.\n다른 날짜를 선택해주세요.`, 
          '매칭 불가능한 날짜'
        );
        return;
      }
    }

    try {
      const { dayId, timeSlot, id } = selectedSchedule;
      
      // Firestore에 실제 업데이트 (학생명과 수업 종류 변경, 관리자 이름 포함)
      await updateScheduleStatus(id, 'confirmed', studentName.trim(), `${user.name} 관리자`, lessonType, lessonDate);
      
      // 로컬 상태 업데이트
      setScheduleData(prev => ({
        ...prev,
        [dayId]: {
          ...prev[dayId],
          [timeSlot]: prev[dayId][timeSlot].map(schedule => 
            schedule.id === id 
              ? {
                  ...schedule,
                  studentName: studentName.trim(),
                  lessonType: lessonType, // 수업 종류 추가
                  lessonDate: lessonDate, // 체험수업 날짜 추가
                  updatedAt: new Date().toISOString(),
                  updatedBy: 'admin'
                }
              : schedule
          )
        }
      }));

      setShowModal(false);
      setSelectedSchedule(null);
      setStudentName('');
      await showSuccess('학생 정보가 수정되었습니다!');
    } catch (error) {
      console.error('학생 정보 수정 오류:', error);
      await showError('학생 정보 수정에 실패했습니다. 다시 시도해주세요.');
    }
  };

        // 학생 정보 삭제 (매칭 대기 상태로 변경)
  const handleRemoveStudent = async () => {
    try {
      const { dayId, timeSlot, id } = selectedSchedule;
      
      // 매칭 대기 메모가 있는지 확인하여 적절한 상태 결정
      const pendingMemoInfo = getPendingMemoInfo(selectedSchedule);
      const hasMatchingMemo = pendingMemos.some(memo => 
        memo && 
        memo.tutorId === selectedSchedule.tutorId &&
        memo.dayOfWeek === selectedSchedule.dayOfWeek && 
        (memo.timeSlot === selectedSchedule.timeSlot || memo.time === selectedSchedule.timeSlot) &&
        memo.memoType === 'pending_memo'
      );
      
      // 매칭 대기 메모가 있으면 pending_with_memo, 없으면 pending 상태로 복원
      const newStatus = hasMatchingMemo ? 'pending_with_memo' : 'pending';
      
      // 메모 등록 상태에서 학생 삭제 시, 연결된 메모도 함께 제거하여 매칭만 해제
      if (selectedSchedule.status === 'registered') {
        try {
          await deleteScheduleMemos(id);
        } catch (e) {
          console.error('메모 삭제 중 오류:', e);
        }
      }
      
      console.log('🔄 학생 삭제 - 상태 복원:', {
        tutorName: selectedSchedule.tutorName,
        dayOfWeek: selectedSchedule.dayOfWeek,
        timeSlot: selectedSchedule.timeSlot,
        hasMatchingMemo,
        newStatus
      });
      
      // Firestore에 실제 업데이트 (적절한 상태로 복원)
      console.log('🔥 Firestore 업데이트 시작:', { id, newStatus });
      await updateScheduleStatus(id, newStatus, '', `${user.name} 관리자`);
      console.log('✅ Firestore 업데이트 완료');
      
      // 로컬 상태 업데이트
      setScheduleData(prev => ({
        ...prev,
        [dayId]: {
          ...prev[dayId],
          [timeSlot]: prev[dayId][timeSlot].map(schedule => 
            schedule.id === id 
              ? {
                  ...schedule,
                  status: newStatus,  // 적절한 상태로 복원
                  studentName: '',
                  lessonType: null,
                  lessonDate: null,
                  confirmedAt: null,
                  confirmedBy: null,
                  updatedAt: new Date().toISOString(),
                  updatedBy: 'admin'
                }
              : schedule
          )
        }
      }));

      setShowModal(false);
      setSelectedSchedule(null);
      setStudentName('');
      setIsEditingStudent(false);
      const statusText = hasMatchingMemo ? '매칭대기 메모' : '매칭 대기';
      await showSuccess(`학생 정보가 삭제되고 ${statusText} 상태로 변경되었습니다!`);
    } catch (error) {
      console.error('학생 정보 삭제 오류:', error);
      await showError('학생 정보 삭제에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 스케줄 거부 기능 제거 (튜터가 가능한 시간만 신청하므로 거부할 이유가 없음)

  // 체험수업 만료 갱신
  const handleExpireTrialLessons = async () => {
    try {
      setLoading(true);
      const expiredCount = await expireTrialLessons();
      if (expiredCount > 0) {
        showSuccess(`${expiredCount}개의 체험수업이 만료되었습니다.`);
      } else {
        showInfo('만료할 체험수업이 없습니다.');
      }
    } catch (error) {
      console.error('체험수업 만료 오류:', error);
      showError('체험수업 만료 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 특정 시간대의 스케줄 리스트 확인
  const getScheduleList = (dayId, timeSlot) => {
    // scheduleData가 null이거나 유효하지 않은 경우 빈 배열 반환
    if (!scheduleData || typeof scheduleData !== 'object') {
      return [];
    }
    
    const daySchedules = scheduleData[dayId];
    if (!daySchedules) return [];
    
    let schedules = daySchedules[timeSlot];
    if (!schedules || !Array.isArray(schedules)) return [];
    
    // 검색어 필터링
    if (searchTerm.trim()) {
      schedules = schedules.filter(schedule =>
        schedule && (
          schedule.tutorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          schedule.studentName?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    
    // 필터 타입별 필터링
    switch (filterType) {
      case 'competition':
        // 경시 튜터 스케줄
        schedules = schedules.filter(schedule => {
          if (!schedule || !schedule.tutorName) return false;
          const tutor = tutors.find(t => t.name === schedule.tutorName);
          const isCompetitionTutor = tutor && tutor.hasCompetition === true;
          console.log(`🏆 경시 필터링: ${schedule.tutorName} - ${isCompetitionTutor ? '경시 튜터' : '일반 튜터'}`);
          return isCompetitionTutor;
        });
        break;
      case 'memo':
        // 메모가 등록된 튜터들 스케줄
        schedules = schedules.filter(schedule => 
          schedule && schedule.status === 'registered'
        );
        break;
      case 'confirmed':
        // 확정완료된 튜터들 스케줄
        schedules = schedules.filter(schedule => 
          schedule && schedule.status === 'confirmed'
        );
        break;
      case 'pending':
        // 매칭 대기 중인 튜터들 스케줄
        schedules = schedules.filter(schedule => 
          schedule && schedule.status === 'pending'
        );
        break;
      case 'all':
      default:
        // 전체 스케줄 (필터링 없음)
        break;
    }
    
    return schedules;
  };

  // 통계 계산
  const getStats = () => {
    // 기본값 반환
    const defaultStats = {
      pendingCount: 0,
      confirmedCount: 0,
      registeredCount: 0
    };

    // scheduleData가 없거나 유효하지 않은 경우 기본값 반환
    if (!scheduleData || typeof scheduleData !== 'object') {
      return defaultStats;
    }

    // 원본 데이터에서 직접 계산 (그룹화된 데이터가 아닌)
    const allSchedules = [];
    
    try {
      Object.values(scheduleData).forEach(daySchedules => {
        if (daySchedules && typeof daySchedules === 'object') {
          Object.values(daySchedules).forEach(schedules => {
            if (Array.isArray(schedules)) {
              allSchedules.push(...schedules.filter(s => s)); // null/undefined 제거
            }
          });
        }
      });
    } catch (error) {
      console.error('스케줄 데이터 처리 오류:', error);
      return defaultStats;
    }
    
    console.log('🔍 전체 스케줄 개수:', allSchedules.length);
    console.log('🔍 스케줄 상태별 개수:', {
      pending: allSchedules.filter(s => s && s.status === 'pending').length,
      confirmed: allSchedules.filter(s => s && s.status === 'confirmed').length,
      registered: allSchedules.filter(s => s && s.status === 'registered').length
    });
    
    // 중복 제거
    const uniqueSchedules = allSchedules.filter((schedule, index, self) => 
      schedule && index === self.findIndex(s => s && s.id === schedule.id)
    );
    
    console.log('🔍 중복 제거 후 스케줄 개수:', uniqueSchedules.length);
    
    let filteredSchedules = uniqueSchedules;
    
    // 검색어 필터링
    if (searchTerm.trim()) {
      filteredSchedules = filteredSchedules.filter(schedule =>
        schedule && (
          schedule.tutorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          schedule.studentName?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    
    // 필터 타입별 필터링
    switch (filterType) {
      case 'competition':
        // 경시 튜터 스케줄
        filteredSchedules = filteredSchedules.filter(schedule => {
          if (!schedule || !schedule.tutorName) return false;
          const tutor = tutors.find(t => t.name === schedule.tutorName);
          const isCompetitionTutor = tutor && tutor.hasCompetition === true;
          console.log(`🏆 통계 경시 필터링: ${schedule.tutorName} - ${isCompetitionTutor ? '경시 튜터' : '일반 튜터'}`);
          return isCompetitionTutor;
        });
        break;
      case 'memo':
        // 메모가 등록된 튜터들 스케줄
        filteredSchedules = filteredSchedules.filter(schedule => 
          schedule && schedule.status === 'registered'
        );
        break;
      case 'confirmed':
        // 확정완료된 튜터들 스케줄
        filteredSchedules = filteredSchedules.filter(schedule => 
          schedule && schedule.status === 'confirmed'
        );
        break;
      case 'pending':
        // 매칭 대기 중인 튜터들 스케줄
        filteredSchedules = filteredSchedules.filter(schedule => 
          schedule && schedule.status === 'pending'
        );
        break;
      case 'all':
      default:
        // 전체 스케줄 (필터링 없음)
        break;
    }
    
    const result = {
      pendingCount: filteredSchedules.filter(s => s && s.status === 'pending').length,
      confirmedCount: filteredSchedules.filter(s => s && s.status === 'confirmed').length,
      registeredCount: filteredSchedules.filter(s => s && s.status === 'registered').length
    };
    
    console.log('📊 최종 통계:', result);
    
    return result;
  };


  // 현재 주차 정보 계산
  const getCurrentWeekInfo = () => {
    const { monday, nextMonday } = getWeekCalculation();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = monday;
    const endDate = nextMonday;
    endDate.setDate(endDate.getDate() - 1); // 일요일로 맞추기

    const formatDate = (date) => {
      return `${date.getMonth() + 1}/${date.getDate()}`;
    };
    
    return {
      start: formatDate(startDate),
      end: formatDate(endDate),
      fullRange: `${formatDate(startDate)} ~ ${formatDate(endDate)}`
    };
  };

  // 이번 주 각 요일의 실제 날짜 계산
  const getWeekDates = () => {
    const { monday, getDateForDayOfWeek } = getWeekCalculation();
    const result = {};
    const dayIds = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    dayIds.forEach(dayId => {
      const date = getDateForDayOfWeek(dayId);
      result[dayId] = {
        date: date,
        formatted: `${date.getMonth() + 1}/${date.getDate()}`
      };
    });
    return result;
  };

  
  // 경시 튜터 목록 가져오기
  const getCompetitionTutorList = () => {
    return tutors
      .filter(tutor => tutor.hasCompetition === true)
      .map(tutor => tutor.name)
      .sort();
  };

  // 안전한 통계 계산 - 데이터가 로드되지 않았으면 기본값 사용
  const stats = (() => {
    if (loading || !scheduleData) {
      return { pendingCount: 0, confirmedCount: 0, registeredCount: 0 };
    }
    try {
      return getStats();
    } catch (error) {
      console.error('통계 계산 오류:', error);
      return { pendingCount: 0, confirmedCount: 0, registeredCount: 0 };
    }
  })();
  const { pendingCount, confirmedCount, registeredCount } = stats;

  // 중복 스케줄 정리 함수


  return (
    <div className="schedule-management">
      <div className="schedule-management-header">
        <div className="header-main">
          <h2>📅 스케줄 관리</h2>
        </div>
        <p>요일별 반복 스케줄을 관리하고 튜터별 학생을 배정해주세요.</p>
      </div>

      {/* 액션 버튼 */}
      <div className="action-buttons">
        <button className="action-btn refresh-btn" onClick={() => window.location.reload()}>
          🔄 새로고침
        </button>
        <button className="action-btn expire-trial-btn" onClick={handleExpireTrialLessons} disabled={loading}>
          ⏰ 체험 만료 갱신
        </button>
        <button className="action-btn add-schedule-btn" onClick={() => setShowAddModal(true)}>
          ➕ 스케줄 등록
        </button>
      </div>

      {/* 필터 */}
      <div className="schedule-filters">
        <div className="filter-controls">
          <div className="search-section">
            <input
              type="text"
              placeholder="🔍 튜터명 또는 학생명으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-section">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">📊 전체 스케줄</option>
              <option value="competition">🏆 경시 튜터 스케줄 ({getCompetitionTutorList().length}명)</option>
              <option value="memo">📝 메모가 등록된 튜터들 스케줄</option>
              <option value="confirmed">✅ 확정완료된 튜터들 스케줄</option>
              <option value="pending">⏳ 매칭 대기 중인 튜터들 스케줄</option>
            </select>
          </div>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="schedule-stats">
        <div className="stat-card pending">
          <div className="stat-number">{pendingCount}</div>
          <div className="stat-label">매칭 대기</div>
        </div>
        <div className="stat-card confirmed">
          <div className="stat-number">{confirmedCount}</div>
          <div className="stat-label">확정 완료</div>
        </div>
        <div className="stat-card registered">
          <div className="stat-number">{registeredCount}</div>
          <div className="stat-label">메모 등록</div>
        </div>
      </div>

      {/* 범례 */}
      <div className="schedule-legend">
        <div className="legend-item">
          <div className="legend-tutor pending">대기</div>
          <span>매칭 대기 (클릭하여 학생 배정)</span>
        </div>
        <div className="legend-item">
          <div className="legend-tutor confirmed">확정</div>
          <span>확정 완료</span>
        </div>
        <div className="legend-item">
          <div className="legend-tutor has-memo">메모</div>
          <span>메모 등록</span>
        </div>
        <div className="legend-item">
          <div className="legend-tutor unavailable-memo">매칭 메모</div>
          <span>매칭 불가능</span>
        </div>
        <div className="legend-item">
          <div className="multiple-indicator">3명 대기</div>
          <span>같은 시간대 매칭 대기 튜터</span>
        </div>
        <div className="legend-item">
          <div className="legend-badge regular">정규</div>
          <span>정규수업</span>
        </div>
        <div className="legend-item">
          <div className="legend-badge trial">체험</div>
          <span>체험수업</span>
        </div>
      </div>

      {loading || !scheduleData ? (
        <div className="loading">📊 스케줄을 불러오는 중...</div>
      ) : Object.keys(scheduleData).length === 0 ? (
        <div className="no-data">
          <div className="no-data-icon">📅</div>
          <h3>등록된 스케줄이 없습니다</h3>
          <p>튜터가 희망 시간을 등록하면 여기에 표시됩니다.</p>
        </div>
      ) : (
        <div className="schedule-calendar">
          <div className="calendar-header">
            {weekDays.map((day) => {
              const weekDates = getWeekDates();
              return (
                <div key={day.id} className="calendar-day-header">
                  <div className="day-name">{day.name}</div>
                  <div className="day-date">{weekDates[day.id].formatted}</div>
                </div>
              );
            })}
          </div>
          
          <div className="calendar-body">
            <div className="time-slots">
              {timeSlots.map(timeSlot => (
                <div key={timeSlot} className="time-slot-row">
                  <div className="time-label">{timeSlot}</div>
                  {weekDays.map(day => {
                    let schedules = [];
                    if (loading || !scheduleData) {
                      schedules = [];
                    } else {
                      try {
                        schedules = getScheduleList(day.id, timeSlot);
                      } catch (error) {
                        console.error('스케줄 목록 로드 오류:', error);
                        schedules = [];
                      }
                    }
                    
                    const hasMultiple = schedules.length > 1;
                    const hasPending = schedules.some(s => s && s.status === 'pending');
                    const hasConfirmed = schedules.some(s => s && s.status === 'confirmed');
                    const hasRegistered = schedules.some(s => s && s.status === 'registered');
                    
                    let cellClass = 'schedule-cell';
                    if (schedules.length > 0) {
                      const statusCount = (hasPending ? 1 : 0) + (hasConfirmed ? 1 : 0) + (hasRegistered ? 1 : 0);
                      if (statusCount > 1) {
                        cellClass += ' mixed';
                      } else if (hasPending) {
                        cellClass += ' pending';
                      } else if (hasConfirmed) {
                        cellClass += ' confirmed';
                      } else if (hasRegistered) {
                        cellClass += ' registered';
                      }
                      if (hasMultiple) {
                        cellClass += ' multiple';
                      }
                    }
                    
                    return (
                      <div 
                        key={`${day.id}-${timeSlot}`}
                        className={cellClass}
                      >
                        {schedules.length > 0 && (
                          <div className="schedule-content">
                            {(() => {
                              const pendingCount = schedules.filter(schedule => schedule.status === 'pending').length;
                              return pendingCount > 0 ? (
                                <div className="multiple-count">
                                  {pendingCount}명 대기
                                </div>
                              ) : null;
                            })()}
                            {schedules.map((schedule, index) => {
                              // 매칭대기 메모 정보 가져오기
                              const pendingMemoInfo = getPendingMemoInfo(schedule);
                              
                              // 실제 상태 계산 (동적으로 pending/pending_with_memo 결정)
                              const actualStatus = getActualScheduleStatus(schedule);
                              
                              // 강경효 튜터만 상세 디버깅
                              if (schedule.tutorName === '강경효') {
                                console.log('🔥 강경효 튜터 상세 디버깅:', {
                                  tutorName: schedule.tutorName,
                                  tutorId: schedule.tutorId,
                                  originalStatus: schedule.status,
                                  actualStatus: actualStatus,
                                  dayOfWeek: schedule.dayOfWeek,
                                  timeSlot: schedule.timeSlot,
                                  pendingMemoInfo: pendingMemoInfo,
                                  isAnyWeekUnavailable: pendingMemoInfo?.isAnyWeekUnavailable,
                                  unavailableDates: pendingMemoInfo?.unavailableDates,
                                  pendingMemosCount: pendingMemos.length,
                                  allPendingMemos: pendingMemos.filter(m => m.tutorName === '강경효')
                                });
                              }
                              
                              return (
                              <div 
                                key={schedule.id}
                                className={`tutor-item ${actualStatus} ${actualStatus === 'registered' ? 'has-memo' : ''}`}
                                onClick={() => handleScheduleClick(day.id, timeSlot, { ...schedule, status: actualStatus })}
                                style={{ cursor: 'pointer' }}
                              >
                                <div className={`tutor-name ${pendingMemoInfo?.isAnyWeekUnavailable ? 'unavailable-tutor' : ''}`}>
                                  {schedule.tutorName}
                                </div>
                                {/* 매칭대기 메모의 불가능한 날짜 표시 */}
                                {pendingMemoInfo && pendingMemoInfo.unavailableDates && pendingMemoInfo.unavailableDates.length > 0 && (
                                  <div className="unavailable-dates">
                                    🚫 {formatUnavailableDates(pendingMemoInfo.unavailableDates)}
                                  </div>
                                )}
                                {(schedule.status === 'confirmed' || schedule.status === 'registered') && (
                                  <div className="student-name">
                                    {schedule.status === 'confirmed' 
                                      ? schedule.studentName 
                                      : (schedule.memoInfo?.student || '학생 정보 없음')
                                    }
                                  </div>
                                )}
                                {(schedule.status === 'confirmed' || schedule.status === 'registered') && (
                                  <div className="schedule-badges-small">
                                    <div className={`lesson-type-badge-small ${schedule.lessonType || 'regular'}`}>
                                      {schedule.lessonType === 'trial' ? '체험' : '정규'}
                                    </div>
                                    {schedule.lessonType === 'trial' && schedule.lessonDate && (
                                      <div className="lesson-date-badge-small">
                                        {new Date(schedule.lessonDate).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                                      </div>
                                    )}
                                  </div>
                                )}

                              </div>
                            );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 스케줄 확정 모달 */}
      {showModal && selectedSchedule && (
        <ScheduleModal
          schedule={selectedSchedule}
          studentName={studentName}
          setStudentName={setStudentName}
          lessonType={lessonType}
          setLessonType={setLessonType}
          lessonDate={lessonDate}
          setLessonDate={setLessonDate}
          onConfirm={handleConfirmSchedule}
          onUpdateStudent={handleUpdateStudent}
          onRemoveStudent={handleRemoveStudent}
          onDelete={handleDeleteSchedule}
          onClose={() => {
            setShowModal(false);
            setSelectedSchedule(null);
            setStudentName('');
            setLessonType('');
            setLessonDate('');
            setIsEditingStudent(false);
          }}
          showConfirm={showConfirm}
          getReasonText={getReasonText}
        />
      )}

      {/* 스케줄 등록 모달 */}
      {showAddModal && (
        <AddScheduleModal
          newSchedule={newSchedule}
          setNewSchedule={setNewSchedule}
          tutorList={tutorList}
          weekDays={weekDays}
          timeSlots={timeSlots}
          onAdd={handleAddSchedule}
          onClose={() => {
            setShowAddModal(false);
            setNewSchedule({ tutorName: '', dayId: '', timeSlot: '' });
            setShowTutorSuggestions(false);
            setFilteredTutors([]);
          }}
          showTutorSuggestions={showTutorSuggestions}
          filteredTutors={filteredTutors}
          onTutorNameChange={handleTutorNameChange}
          onTutorSelect={handleTutorSelect}
        />
      )}

      {/* 커스텀 알림 모달 */}
      <CustomAlert {...alertState} />
    </div>
  );
};

// 스케줄 확정 모달 컴포넌트
const ScheduleModal = ({ schedule, studentName, setStudentName, lessonType, setLessonType, lessonDate, setLessonDate, onConfirm, onUpdateStudent, onRemoveStudent, onDelete, onClose, showConfirm, getReasonText }) => {
  const [isEditingStudent, setIsEditingStudent] = useState(false);
  const getDayKorean = (dayId) => {
    const map = {
      monday: '월요일',
      tuesday: '화요일',
      wednesday: '수요일',
      thursday: '목요일',
      friday: '금요일',
      saturday: '토요일',
      sunday: '일요일'
    };
    return map[dayId] || '';
  };
  const dayText = getDayKorean(schedule.dayOfWeek || schedule.dayId);

  const formatUnavailableList = (unavailableDates) => {
    if (!unavailableDates || unavailableDates.length === 0) return '';
    return unavailableDates
      .slice()
      .sort((a, b) => new Date(a) - new Date(b))
      .map((dateStr) => {
        const date = new Date(dateStr);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      })
      .join(', ');
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📝 스케줄 신청 처리</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="schedule-detail">
            <div className="detail-row">
              <span className="detail-label">👨‍🏫 튜터:</span>
              <span className="detail-value">{schedule.tutorName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">⏰ 시간:</span>
              <span className="detail-value">{dayText ? `${dayText} ${schedule.timeSlot}` : schedule.timeSlot}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">📅 신청일시:</span>
              <span className="detail-value">
                {schedule.requestedAt ? 
                  (schedule.requestedAt.toDate ? 
                    schedule.requestedAt.toDate().toLocaleString('ko-KR') : 
                    new Date(schedule.requestedAt).toLocaleString('ko-KR')
                  ) : 
                  '날짜 정보 없음'
                }
                <br />
                <small className="created-by">
                  {schedule.requestedBy === 'admin' ? '👩‍💼 관리자 등록' : 
                   schedule.requestedBy && schedule.requestedBy.includes('관리자') ? `👩‍💼 ${schedule.requestedBy}` : 
                   '📋 튜터 신청'}
                </small>
              </span>
            </div>
            {schedule.status === 'confirmed' && (
              <div className="detail-row">
                <span className="detail-label">✅ 확정일시:</span>
                <span className="detail-value">
                  {schedule.confirmedAt ? 
                    (schedule.confirmedAt.toDate ? 
                      schedule.confirmedAt.toDate().toLocaleString('ko-KR') : 
                      new Date(schedule.confirmedAt).toLocaleString('ko-KR')
                    ) : 
                    '날짜 정보 없음'
                  }
                  <br />
                  <small className="created-by">
                    {schedule.confirmedBy === 'admin' ? '👩‍💼 관리자 확정' : 
                     schedule.confirmedBy && schedule.confirmedBy.includes('관리자') ? `👩‍💼 ${schedule.confirmedBy}` : 
                     '📋 튜터 확정'}
                  </small>
                </span>
              </div>
            )}
            {/* 매칭대기 메모 정보 표시 (수업 불가능 날짜/사유) */}
            {(schedule.status === 'pending' || schedule.status === 'pending_with_memo') && schedule.memoInfo && (
              <>
                <div className="detail-row">
                  <span className="detail-label">📅 수업 불가능 날짜:</span>
                  <span className="detail-value">{formatUnavailableList(schedule.memoInfo.unavailableDates)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">📝 사유:</span>
                  <span className="detail-value">{schedule.memoInfo.reason || schedule.memoInfo.memo || '사유 없음'}</span>
                </div>
              </>
            )}
            {schedule.status === 'registered' && schedule.memoInfo && (
              <>
                <div className="detail-row">
                  <span className="detail-label">👨‍🎓 학생:</span>
                  <span className="detail-value">
                    {schedule.memoInfo.student || '학생 정보 없음'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">📝 메모:</span>
                  <span className="detail-value memo-content">
                    <div className="memo-reason">
                      <strong>사유:</strong> {getReasonText(schedule.memoInfo.reason)}
                    </div>
                    <div className="memo-text">
                      <strong>내용:</strong> {schedule.memoInfo.memo}
                    </div>
                    <small className="memo-created-by">
                      📋 {schedule.memoInfo.tutorName} 작성 · {
                        schedule.memoInfo.timestamp ? 
                          (schedule.memoInfo.timestamp.toDate ? 
                            schedule.memoInfo.timestamp.toDate().toLocaleString('ko-KR') : 
                            new Date(schedule.memoInfo.timestamp).toLocaleString('ko-KR')
                          ) : 
                          '날짜 정보 없음'
                      }
                    </small>
                  </span>
                </div>
              </>
            )}
          </div>

          {(schedule.status === 'pending' || schedule.status === 'confirmed') && (
            <>
              <div className="student-assignment">
                <label htmlFor="student-input">
                  {schedule.status === 'pending' ? '👨‍🎓 배정할 학생명' : '👨‍🎓 학생명 수정'}
                </label>
                {schedule.status === 'pending' ? (
                  <input
                    id="student-input"
                    type="text"
                    placeholder="학생명을 입력하세요"
                    value={studentName}
                    onChange={(e) => setStudentName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && studentName.trim()) {
                        onConfirm();
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <div className="student-input-container">
                    {!isEditingStudent ? (
                      <div 
                        className="student-display"
                        onClick={() => setIsEditingStudent(true)}
                      >
                        {studentName || '클릭하여 수정'}
                      </div>
                    ) : (
                      <input
                        id="student-input"
                        type="text"
                        placeholder="학생명을 입력하세요"
                        value={studentName}
                        onChange={(e) => setStudentName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && studentName.trim()) {
                            onUpdateStudent();
                          }
                        }}
                        onBlur={() => setIsEditingStudent(false)}
                        autoFocus
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="lesson-assignment-container">
                <div className="lesson-type-assignment">
                  <label>수업 종류</label>
                  {schedule.status === 'pending' ? (
                    <>
                      <div className="button-group">
                        <button
                          type="button"
                          className={`type-button ${lessonType === 'regular' ? 'active' : ''}`}
                          onClick={() => setLessonType('regular')}
                        >
                          정규수업
                        </button>
                        <button
                          type="button"
                          className={`type-button ${lessonType === 'trial' ? 'active' : ''}`}
                          onClick={() => setLessonType('trial')}
                        >
                          체험수업
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="lesson-type-display">
                      <span className={`type-display ${lessonType === 'regular' ? 'regular' : 'trial'}`}>
                        {lessonType === 'regular' ? '정규수업' : '체험수업'}
                      </span>
                    </div>
                  )}
                </div>

                {/* 체험수업 날짜 선택 */}
                {lessonType === 'trial' && (
                  <div className="lesson-date-assignment">
                    <label>수업 날짜</label>
                    {schedule.status === 'pending' ? (
                      <input
                        type="date"
                        value={lessonDate}
                        onChange={(e) => setLessonDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]} // 오늘 날짜부터 선택 가능
                        required
                      />
                    ) : (
                      <div className="lesson-date-display">
                        <span className="date-display">
                          {lessonDate ? new Date(lessonDate).toLocaleDateString('ko-KR') : '날짜 미설정'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          

        </div>

        <div className="modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>
            취소
          </button>
          
          {/* 삭제 버튼 (모든 스케줄에 대해 표시) */}
          <button 
            className="modal-delete-btn" 
            onClick={async () => {
              const confirmed = await showConfirm(
                '정말로 이 스케줄을 삭제하시겠습니까?\n관련 메모도 함께 삭제됩니다.',
                '스케줄 삭제 확인',
                '삭제하기',
                '돌아가기'
              );
              if (confirmed) {
                onDelete(schedule.dayId, schedule.timeSlot, schedule.id);
                onClose();
              }
            }}
          >
            🗑️ 스케줄 삭제
          </button>
          
          {/* 확정 버튼 (pending 상태인 경우에만 표시) */}
          {schedule.status === 'pending' && (
            <button 
              className="modal-confirm-btn" 
              onClick={() => {
                console.log('🖱️ 학생 배정 버튼 클릭됨');
                onConfirm();
              }}
              disabled={!studentName.trim() || !lessonType}
            >
              ✅ 학생 배정
            </button>
          )}
          
          {/* 수정 버튼 (confirmed 상태인 경우에만 표시) */}
          {schedule.status === 'confirmed' && (
            <>
              <button 
                className="modal-confirm-btn" 
                onClick={onUpdateStudent}
                disabled={!studentName.trim() || !lessonType}
              >
                ✏️ 학생 수정
              </button>
              <button 
                className="modal-remove-btn" 
                onClick={async () => {
                  const confirmed = await showConfirm(
                    '정말로 이 학생 정보를 삭제하시겠습니까?\n스케줄은 매칭 대기 상태로 변경됩니다.',
                    '학생 정보 삭제 확인',
                    '삭제하기',
                    '돌아가기'
                  );
                  if (confirmed) {
                    onRemoveStudent();
                  }
                }}
              >
                🗑️ 학생 삭제
              </button>
            </>
          )}

          {/* 메모 등록(registered) 상태에서도 학생 삭제 가능 */}
          {schedule.status === 'registered' && (
            <button 
              className="modal-remove-btn" 
              onClick={async () => {
                const confirmed = await showConfirm(
                  '정말로 이 학생 정보를 삭제하시겠습니까?\n메모는 정리되고 스케줄은 매칭 대기 상태로 변경됩니다.',
                  '학생 정보 삭제 확인',
                  '삭제하기',
                  '돌아가기'
                );
                if (confirmed) {
                  onRemoveStudent();
                }
              }}
            >
              🗑️ 학생 삭제
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// 스케줄 등록 모달 컴포넌트
const AddScheduleModal = ({ 
  newSchedule, 
  setNewSchedule, 
  tutorList, 
  weekDays, 
  timeSlots, 
  onAdd, 
  onClose,
  showTutorSuggestions,
  filteredTutors,
  onTutorNameChange,
  onTutorSelect
}) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>➕ 새 스케줄 등록</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="add-schedule-form">
            <div className="form-group">
              <label htmlFor="tutor-input">👨‍🏫 튜터명</label>
              <div className="tutor-input-container">
                <input
                  id="tutor-input"
                  type="text"
                  placeholder="튜터명을 입력하세요"
                  value={newSchedule.tutorName}
                  onChange={(e) => onTutorNameChange(e.target.value)}
                  onFocus={() => {
                    if (newSchedule.tutorName.trim() && tutorList && Array.isArray(tutorList)) {
                      const filtered = tutorList.filter(tutor => 
                        tutor.name && tutor.name.toLowerCase().includes(newSchedule.tutorName.toLowerCase())
                      );
                      if (filtered.length > 0) {
                        onTutorNameChange(newSchedule.tutorName);
                      }
                    }
                  }}
                  autoFocus
                />
                {showTutorSuggestions && filteredTutors && Array.isArray(filteredTutors) && (
                  <div className="tutor-suggestions">
                    {filteredTutors.map(tutor => (
                      <div 
                        key={tutor.id || tutor.name} 
                        className="tutor-suggestion-item"
                        onClick={() => onTutorSelect(tutor)}
                      >
                        <div className="tutor-name">{tutor.name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="day-select">📅 요일 선택</label>
              <select
                id="day-select"
                value={newSchedule.dayId}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, dayId: e.target.value }))}
              >
                <option value="">요일을 선택하세요</option>
                {weekDays.map(day => (
                  <option key={day.id} value={day.id}>{day.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="time-select">⏰ 시간 선택</label>
              <select
                id="time-select"
                value={newSchedule.timeSlot}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, timeSlot: e.target.value }))}
              >
                <option value="">시간을 선택하세요</option>
                {timeSlots.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>
            취소
          </button>
          <button 
            className="modal-confirm-btn" 
            onClick={onAdd}
            disabled={!newSchedule.tutorName.trim() || !newSchedule.dayId || !newSchedule.timeSlot}
          >
            ➕ 등록
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleManagement; 